import { createPortal } from "react-dom";

const exact = { WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" };
const rupee = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;

function sizeLabel(it) {
  if (it.matched_name) return it.matched_name;
  if (it.type === "nut") return `${it.dia} NUT`;
  const th = it.thread ? ` ${it.thread}` : "";
  return `${it.dia} × ${it.length}${th}`;
}

export function OrderSheet({ order }) {
  const items = order.items || [];
  const hasVal = (v) => v != null && String(v) !== "";
  const rateOf = (it) => (hasVal(it.final_rate) ? Number(it.final_rate) : hasVal(it.line_rate) ? Number(it.line_rate) : null);
  const hasRates = items.some((it) => rateOf(it) != null);
  const totalBags = items.reduce((s, it) => s + (Number(it.qty_bags) || 0), 0);
  const grand = items.reduce((s, it) => s + (Number(it.qty_bags) || 0) * (rateOf(it) || 0), 0);

  const td = { padding: "3px 6px", border: "1px solid #94a3b8", fontSize: "12.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

  return (
    <div style={{ fontFamily: "Arial, Helvetica, sans-serif", color: "#000" }}>
      <div style={{ padding: "10mm 10mm", boxSizing: "border-box" }}>
        {/* header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "2.5px solid #000", paddingBottom: "6px" }}>
          <div style={{ fontSize: "22px", fontWeight: 900, letterSpacing: "0.04em" }}>QUOTATION</div>
          <div style={{ textAlign: "right", fontSize: "12px" }}>
            <div style={{ fontWeight: 900, fontSize: "14px", padding: "3px 9px", border: "2px solid #0f172a", borderRadius: "5px", background: "#f1f5f9", ...exact }}>
              {order.number || "QUOTATION"}
            </div>
            <div style={{ marginTop: "4px" }} data-testid="print-order-date">Order Date: <b>{order.date || "—"}</b></div>
            {order.delivery_date && <div style={{ marginTop: "1px" }}>Delivery: <b>{order.delivery_date}</b></div>}
          </div>
        </div>

        {/* party */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: "8px", gap: "12px" }}>
          <div style={{ fontSize: "13px", lineHeight: 1.45, minWidth: 0 }}>
            <div><b>Party:</b> <span style={{ fontWeight: 700 }}>{order.party || "—"}</span></div>
            {order.address ? <div data-testid="print-address" style={{ fontSize: "11.5px" }}>{order.address}</div> : null}
            {order.gstin ? <div data-testid="print-gstin" style={{ fontSize: "11.5px" }}><b>GSTIN:</b> {order.gstin}</div> : null}
            <div data-testid="print-transport"><b>Transport:</b> {order.transport || "—"}</div>
          </div>
          <div style={{ fontSize: "13px", textAlign: "right", flexShrink: 0 }}>
            {!hasRates && hasVal(order.bolt_basic) && (
              <div data-testid="print-basic-rate" style={{ fontWeight: 900, fontSize: "14px" }}>
                Basic Rate: {rupee(order.bolt_basic)} / bag
                {hasVal(order.nut_basic) ? <span style={{ fontWeight: 400, fontSize: "12px" }}> &nbsp;·&nbsp; Nut Basic: {rupee(order.nut_basic)}</span> : null}
              </div>
            )}
          </div>
        </div>

        {/* items */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "8px", tableLayout: "fixed" }}>
          <thead>
            <tr style={{ background: "#0f172a", color: "#fff", ...exact }}>
              <th style={{ ...td, border: "1px solid #0f172a", width: "34px", textAlign: "left" }}>#</th>
              <th style={{ ...td, border: "1px solid #0f172a", textAlign: "left" }}>Size</th>
              <th style={{ ...td, border: "1px solid #0f172a", width: hasRates ? "70px" : "110px", textAlign: "right" }}>Qty (Bags)</th>
              <th style={{ ...td, border: "1px solid #0f172a", width: hasRates ? "90px" : "140px", textAlign: "left" }}>Plating</th>
              {hasRates && <th style={{ ...td, border: "1px solid #0f172a", width: "110px", textAlign: "right" }}>Rate / Bag</th>}
              {hasRates && <th style={{ ...td, border: "1px solid #0f172a", width: "110px", textAlign: "right" }}>Amount</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const r = rateOf(it);
              return (
              <tr key={i} style={{ background: i % 2 ? "#f1f5f9" : "#fff", ...exact }}>
                <td style={{ ...td, textAlign: "left" }}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 700 }}>
                  {sizeLabel(it)}{it.remarks ? <span style={{ fontWeight: 400, color: "#475569" }}> ({it.remarks})</span> : null}
                </td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{it.qty_bags}</td>
                <td style={{ ...td }}>{it.plating || ""}</td>
                {hasRates && <td style={{ ...td, textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>{r != null ? rupee(r) : "—"}</td>}
                {hasRates && <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{r != null ? rupee((Number(it.qty_bags) || 0) * r) : "—"}</td>}
              </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "#e2e8f0", fontWeight: 900, ...exact }}>
              <td style={{ ...td, border: "1px solid #0f172a", textAlign: "right" }} colSpan={2}>TOTAL</td>
              <td style={{ ...td, border: "1px solid #0f172a", textAlign: "right" }}>{totalBags}</td>
              <td style={{ ...td, border: "1px solid #0f172a" }}></td>
              {hasRates && <td style={{ ...td, border: "1px solid #0f172a" }}></td>}
              {hasRates && <td style={{ ...td, border: "1px solid #0f172a", textAlign: "right", fontSize: "14px" }}>{rupee(grand)}</td>}
            </tr>
          </tfoot>
        </table>

        {order.remarks ? (
          <div style={{ marginTop: "7px", fontSize: "11.5px" }}><b>Remarks:</b> {order.remarks}</div>
        ) : null}

        <div style={{ marginTop: "16px", fontSize: "12px" }}>
          <div>Customer Confirmation: ____________________</div>
        </div>
      </div>
    </div>
  );
}

export function OrderPrint({ order }) {
  if (!order) return null;
  return createPortal(
    <div id="print-area">
      <OrderSheet order={order} />
    </div>,
    document.body
  );
}
